<template>
  <div class="cart-page">
    <div class="cart-container">
      <!-- Cart Header -->
      <div class="cart-header">
        <h1>Your Cart</h1>
        <p v-if="cartItems.length === 0">Your cart is empty</p>
        <p v-else>{{ cartItems.length }} item{{ cartItems.length > 1 ? 's' : '' }} in your cart</p>
      </div>

      <div class="cart-content" v-if="cartItems.length > 0">
        <!-- Cart Items -->
        <div class="cart-items">
          <div class="cart-item" v-for="item in cartItems" :key="item.id">
            <img
              :src="item.image || item.image_url || placeholderImage"
              :alt="item.name"
              class="item-image"
              @error="handleCartImageError"
            />
            <div class="item-details">
              <h3>{{ item.name }}</h3>
              <p class="item-description">{{ item.description }}</p>
              <div class="item-price-section">
                <div class="item-price" :class="{ 'has-discount': getItemDiscount(item) > 0 }">
                  <span v-if="getItemDiscount(item) > 0" class="original-price">₱{{ item.price }}</span>
                  <span class="current-price">₱{{ getItemDiscountedPrice(item).toFixed(2) }}</span>
                </div>
                <div v-if="getItemDiscount(item) > 0" class="item-discount-info">
                  <span class="discount-badge">{{ getItemDiscountText(item) }}</span>
                  <span class="savings">
                    You save ₱{{ (getItemDiscount(item) * item.quantity).toFixed(2) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="item-controls">
              <button class="quantity-btn" @click="decreaseQuantity(item.id)">-</button>
              <span class="quantity">{{ item.quantity }}</span>
              <button class="quantity-btn" @click="increaseQuantity(item.id)">+</button>
            </div>
            <div class="item-total">
              <div v-if="getItemDiscount(item) > 0" class="total-with-discount">
                <span class="original-total">₱{{ (item.price * item.quantity).toFixed(2) }}</span>
                <span class="discounted-total">
                  ₱{{ (getItemDiscountedPrice(item) * item.quantity).toFixed(2) }}
                </span>
              </div>
              <div v-else class="regular-total">
                ₱{{ (item.price * item.quantity).toFixed(2) }}
              </div>
            </div>
            <button class="remove-btn" @click="removeItem(item.id)">×</button>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="order-summary">
          <h2>Order Summary</h2>
          <div class="summary-row">
            <span>Subtotal</span>
            <span>₱{{ subtotal.toFixed(2) }}</span>
          </div>
          <div class="summary-row" v-if="deliveryType === 'delivery'">
            <span>Delivery Fee</span>
            <span>₱{{ deliveryFee.toFixed(2) }}</span>
          </div>
          <div class="summary-row">
            <span>Service Fee</span>
            <span>₱{{ serviceFee.toFixed(2) }}</span>
          </div>
          
          <!-- Loyalty Points Section -->
          <div class="loyalty-points-section" v-if="userProfile">
            <div class="points-header">
              <div class="points-info">
                <span class="points-icon">⭐</span>
                <span class="points-label">Your Points:</span>
                <span class="points-balance">{{ userProfile.loyalty_points || 0 }}</span>
                <span class="points-value">
                  (₱{{ ((userProfile.loyalty_points || 0) / 4).toFixed(2) }} value)
                </span>
              </div>
            </div>
            
            <div class="points-redemption">
              <label class="points-checkbox">
                <input 
                  type="checkbox" 
                  v-model="useLoyaltyPoints"
                  @change="onPointsToggle"
                />
                <span class="checkbox-text">Use loyalty points</span>
              </label>
              
              <div
                v-if="useLoyaltyPoints && (userProfile.loyalty_points || 0) >= 40"
                class="points-input-group"
              >
                <div class="points-input-wrapper">
                  <input 
                    type="text"
                    v-model="pointsToRedeem"
                    placeholder="Enter points (40-80)"
                    class="points-input"
                    @input="handlePointsInput"
                    @keyup.enter="validatePointsInput"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    ref="pointsInput"
                  />
                  <div class="quick-select-points">
                    <button type="button" @click="setPoints(40)" class="quick-btn">40</button>
                    <button type="button" @click="setPoints(60)" class="quick-btn">60</button>
                    <button type="button" @click="setPoints(80)" class="quick-btn">80</button>
                  </div>
                </div>
                <span class="points-discount">= ₱{{ pointsDiscount.toFixed(2) }} off</span>
                <div class="points-rates">
                  <small>40 pts = ₱10 | 80 pts = ₱20 (max per order)</small>
                </div>
              </div>
              <div
                v-else-if="useLoyaltyPoints && (userProfile.loyalty_points || 0) < 40"
                class="points-insufficient"
              >
                <span class="insufficient-message">
                  ⚠️ You need at least 40 points to redeem (₱10 minimum)
                </span>
              </div>
            </div>
          </div>

          <!-- Promotion Code Input -->
          <div class="promo-code-section">
            <div class="promo-input-group">
              <input 
                type="text" 
                v-model="promoCode" 
                placeholder="Enter promo code"
                class="promo-input"
                :disabled="appliedPromotion"
                @keyup.enter="applyPromoCode"
              />
              <button 
                v-if="!appliedPromotion"
                class="apply-promo-btn" 
                @click="applyPromoCode"
                :disabled="!promoCode || applyingPromo"
              >
                <span v-if="!applyingPromo">Apply</span>
                <span v-else class="loading-spinner-small"></span>
              </button>
              <button 
                v-else
                class="remove-promo-btn" 
                @click="removePromoCode"
              >
                Remove
              </button>
            </div>
            
            <!-- Promo Error Message -->
            <div v-if="promoError" class="promo-error">
              <span class="error-icon">⚠️</span>
              {{ promoError }}
            </div>
          </div>
          
          <!-- Points Discount Row (if points used) -->
          <div v-if="pointsDiscount > 0" class="summary-row points-discount-row">
            <span>Points Discount</span>
            <span class="discount-amount">-₱{{ pointsDiscount.toFixed(2) }}</span>
          </div>
          
          <!-- Voucher / Promotion Summary -->
          <div v-if="appliedPromotion" class="applied-voucher-summary">
            <div class="voucher-info-row">
              <div class="voucher-details">
                <span class="voucher-icon">🎁</span>
                <div>
                  <div class="voucher-name">
                    {{ getVoucherDisplayName(appliedPromotion) }}
                  </div>
                  <div class="voucher-description">
                    {{ getPromotionDescription(appliedPromotion) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="summary-row discount-row">
              <span>Voucher Discount</span>
              <span class="discount-amount" v-if="promotionDiscount > 0">
                -₱{{ promotionDiscount.toFixed(2) }}
              </span>
              <span class="discount-amount" v-else>
                ₱{{ promotionDiscount.toFixed(2) }}
              </span>
            </div>
          </div>
          
          <div class="summary-row total">
            <span>Total</span>
            <span>₱{{ finalTotal.toFixed(2) }}</span>
          </div>
        </div>

        <!-- Delivery Options -->
        <div class="delivery-options">
          <h2>Delivery Options</h2>
          <div class="option-group">
            <label class="option-label">
              <input type="radio" v-model="deliveryType" value="delivery" />
              <div class="option-content">
                <div class="option-icon">🚚</div>
                <div class="option-text">
                  <h3>Delivery</h3>
                  <p>Get it delivered to your doorstep</p>
                  <span class="option-time">30-45 mins</span>
                </div>
              </div>
            </label>
            <label class="option-label">
              <input type="radio" v-model="deliveryType" value="pickup" />
              <div class="option-content">
                <div class="option-icon">🏪</div>
                <div class="option-text">
                  <h3>Pickup</h3>
                  <p>Pick up from our store</p>
                  <span class="option-time">15-20 mins</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Delivery Address (only show if delivery is selected) -->
        <div class="delivery-address" v-if="deliveryType === 'delivery'">
          <h2>Delivery Address</h2>
          <div class="address-input">
            <input 
              type="text" 
              v-model="deliveryAddress" 
              placeholder="Enter your delivery address"
              class="address-field"
            />
            <button class="map-btn" @click="openMap">📍 Use Map</button>
          </div>
          
          <!-- Google Map -->
          <div class="map-container" v-if="showMap">
            <p class="map-instructions">
              📍 Click anywhere on the map to set your delivery location
            </p>
            <div id="google-map" class="google-map"></div>
            <button class="close-map-btn" @click="closeMap">Close Map</button>
          </div>
        </div>

        <!-- Payment Methods -->
        <div class="payment-methods">
          <h2>Payment Method</h2>
          <div class="payment-options">
            <label class="payment-option">
              <input type="radio" v-model="paymentMethod" value="cash" />
              <div class="payment-content">
                <div class="payment-icon">💵</div>
                <span>Cash on Delivery</span>
              </div>
            </label>
            <label class="payment-option">
              <input type="radio" v-model="paymentMethod" value="gcash" />
              <div class="payment-content">
                <div class="payment-icon">📱</div>
                <span>GCash</span>
              </div>
            </label>
            <label class="payment-option">
              <input type="radio" v-model="paymentMethod" value="card" />
              <div class="payment-content">
                <div class="payment-icon">💳</div>
                <span>Credit/Debit Card</span>
              </div>
            </label>
            <label class="payment-option">
              <input type="radio" v-model="paymentMethod" value="grabpay" />
              <div class="payment-content">
                <div class="payment-icon">🎯</div>
                <span>GrabPay QR</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Special Instructions -->
        <div class="special-instructions">
          <h2>Special Instructions</h2>
          <textarea 
            v-model="specialInstructions" 
            placeholder="Any special requests or instructions for your order..."
            class="instructions-field"
          ></textarea>
        </div>

        <!-- Active Order Warning -->
        <div v-if="activeOrder" class="active-order-warning">
          <div class="warning-icon">⚠️</div>
          <div class="warning-content">
            <strong>You have an ongoing order</strong>
            <p>
              Order <span class="warning-order-id">{{ activeOrder.order_id }}</span>
              is currently <span class="warning-status">{{ activeOrder.status }}</span>.
              Please wait for it to be completed or cancel it before placing a new order.
            </p>
          </div>
        </div>

        <!-- Checkout Button -->
        <div class="checkout-section">
          <button class="checkout-btn" @click="proceedToCheckout" :disabled="!canCheckout">
            <span v-if="isProcessing" class="loading-spinner"></span>
            {{ isProcessing ? 'Processing...' : `Place Order - ₱${finalTotal.toFixed(2)}` }}
          </button>
        </div>
      </div>

      <!-- Empty Cart State -->
      <div class="empty-cart" v-else>
        <div class="empty-cart-icon">🛒</div>
        <h2>Your cart is empty</h2>
        <p>Add some delicious ramyeon to get started!</p>
        <button class="browse-menu-btn" @click="$emit('setCurrentPage', 'Menu')">
          Browse Menu
        </button>
      </div>
    </div>

    <!-- Order Confirmation Modal -->
    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="showOrderConfirmation"
          class="confirmation-modal-overlay"
          @click="closeConfirmationModal"
        >
          <div class="confirmation-modal" @click.stop>
            <div class="confirmation-success-icon">
              <div class="checkmark-circle">
                <div class="checkmark"></div>
              </div>
            </div>
            
            <h2 class="confirmation-title">Order Placed Successfully! 🎉</h2>
          
            <div class="confirmation-details">
              <div class="detail-row">
                <span class="detail-label">Order ID:</span>
                <span class="detail-value order-id">{{ confirmedOrder.id }}</span>
              </div>
              
              <div class="detail-row">
                <span class="detail-label">Total Amount:</span>
                <span class="detail-value total-amount">
                  ₱{{ confirmedOrder.total }}
                </span>
              </div>
              
              <div class="detail-row">
                <span class="detail-label">Payment Method:</span>
                <span class="detail-value">
                  {{ confirmedOrder.paymentMethod?.toUpperCase() }}
                </span>
              </div>
              
              <div class="detail-row">
                <span class="detail-label">Delivery Type:</span>
                <span class="detail-value">
                  {{ confirmedOrder.deliveryType === 'delivery' ? 'Delivery 🚚' : 'Pickup 🏪' }}
                </span>
              </div>
              
              <div class="detail-row estimated-time">
                <span class="detail-label">Estimated Time:</span>
                <span class="detail-value">
                  {{ confirmedOrder.deliveryType === 'delivery' ? '30-45' : '15-20' }} minutes
                </span>
              </div>
              
              <!-- Loyalty Points Info -->
              <div
                v-if="confirmedOrder.pointsEarned > 0 || confirmedOrder.pointsUsed > 0"
                class="loyalty-points-summary"
              >
                <div v-if="confirmedOrder.pointsUsed > 0" class="detail-row">
                  <span class="detail-label">Points Used:</span>
                  <span class="detail-value points-used">
                    -{{ confirmedOrder.pointsUsed }} points
                  </span>
                </div>
                <div v-if="confirmedOrder.pointsEarned > 0" class="detail-row">
                  <span class="detail-label">Points Earned:</span>
                  <span class="detail-value points-earned">
                    +{{ confirmedOrder.pointsEarned }} points
                  </span>
                </div>
              </div>
            </div>
          
            <div class="confirmation-message">
              <p>✅ Your order has been sent to our kitchen!</p>
              <p>📱 You'll receive updates on your order status.</p>
              <p v-if="confirmedOrder.pointsEarned > 0">
                ⭐ You earned {{ confirmedOrder.pointsEarned }} loyalty points!
              </p>
            </div>
          
            <button class="confirmation-btn" @click="goToHome">
              Back to Home
            </button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { paymongoAPI } from '../composables/usePaymongo.js';
import { authAPI, ordersAPI } from '../services/api.js';
import { useOnlineOrder } from '../composables/api/useOnlineOrder.js';
import { useProducts } from '../composables/api/useProducts.js';
import { usePromotions } from '../composables/api/usePromotions.js';
import { useLoyalty } from '../composables/api/useLoyalty.js';

// Toggle verbose cart logging
const CART_DEBUG = (
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_DEBUG_CART === 'true') ||
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.VUE_APP_DEBUG_CART === 'true')
);

export default {
  name: 'Cart',
  emits: ['setCurrentPage', 'cartCleared'],
  setup() {
    const onlineOrder = useOnlineOrder();
    const products = useProducts();
    const promotions = usePromotions();
    const loyalty = useLoyalty();
    
    return {
      ...onlineOrder,
      ...products,
      ...promotions,
      ...loyalty
    };
  },
  data() {
    return {
      deliveryType: 'delivery',
      deliveryAddress: '',
      paymentMethod: 'cash',
      specialInstructions: '',
      showMap: false,
      isProcessing: false,
      map: null,
      marker: null,
      showCardForm: false,
      cardDetails: {
        number: '',
        exp_month: '',
        exp_year: '',
        cvc: '',
        name: ''
      },
      placeholderImage: require('../assets/Home/BigRamen.png'),
      userProfile: null,
      isUserLoading: false,
      showOrderConfirmation: false,
      confirmedOrder: {},
      promoCode: '',
      appliedPromotion: null,
      promotionDiscount: 0,
      applyingPromo: false,
      promoError: null,
      useLoyaltyPoints: false,
      pointsToRedeem: '',
      pointsDiscount: 0,
      maxPointsToRedeem: 80,
      pointsApplied: false,
      pendingOrder: null,
      pendingOrderKey: 'ramyeon_pending_order',
      // 🌍 Global / auto promotions (subset of activePromotions)
      globalPromotions: [],
      activeOrder: null,
      checkingActiveOrder: false,
    };
  },
  computed: {
    // Expose cartStore if using Vuex / Pinia-like store
    cartStore() {
      return this.$store?.state?.cart || null;
    },
    
    subtotal() {
      return this.cartItems.reduce((total, item) => {
        const discountedPrice = this.getItemDiscountedPrice(item);
        return total + (discountedPrice * item.quantity);
      }, 0);
    },
    deliveryFee() {
      return this.deliveryType === 'delivery' ? this.cartShipping : 0;
    },
    serviceFee() {
      return this.cartTax;
    },
    total() {
      return this.cartTotal;
    },
    finalTotal() {
      const shippingFee = this.deliveryFee;
      const baseTotal = this.subtotal + this.serviceFee + shippingFee;

      const totalDiscount =
        (this.pointsDiscount || 0) +
        (this.promotionDiscount || 0);

      return Math.max(0, baseTotal - totalDiscount);
    },
    canCheckout() {
      const hasItems = this.cartItems.length > 0;
      const hasPayment = this.paymentMethod !== '';
      const hasAddress =
        this.deliveryType === 'pickup' ||
        (this.deliveryType === 'delivery' && this.deliveryAddress.trim() !== '');
      return hasItems && hasPayment && hasAddress && !this.isProcessing && !this.activeOrder;
    }
  },
  methods: {
    // ✅ NEW: derive global promotions from activePromotions
    loadGlobalPromotions() {
      if (!this.activePromotions || !this.activePromotions.length) {
        this.globalPromotions = [];
        return;
      }

      const now = new Date();

      this.globalPromotions = this.activePromotions.filter(promo => {
        if (!promo || promo.isDeleted) return false;
        if (promo.name === 'PWD' || promo.name === 'Senior Citizen') return false;
        if (promo.status !== 'active') return false;

        const targetType = (promo.target_type || '').toLowerCase();
        if (targetType !== 'all' && targetType !== 'category' && targetType !== 'categories') {
          return false;
        }

        if (promo.start_date && promo.end_date) {
          const start = new Date(promo.start_date);
          const end = new Date(promo.end_date);
          if (now < start || now > end) return false;
        }

        return true;
      });
    },

    // Check for loyalty points intent pre-selected from profile (localStorage)
    checkForLoyaltyIntent() {
      try {
        const intent = localStorage.getItem('ramyeon_use_loyalty_points')
        if (!intent) return
        localStorage.removeItem('ramyeon_use_loyalty_points')

        const { points } = JSON.parse(intent)
        const userPoints = this.userProfile?.loyalty_points || 0
        if (!points || points < 40 || userPoints < 40) return

        this.useLoyaltyPoints = true
        this.onPointsToggle()
        this.$nextTick(() => {
          this.setPoints(Math.min(points, this.maxPointsToRedeem || 80))
          this.validatePointsInput()
        })
        this.showSuccessNotification(`${points} loyalty points applied to this order!`)
      } catch (e) {
        localStorage.removeItem('ramyeon_use_loyalty_points')
      }
    },

    // Check for voucher pre-selected from profile (localStorage)
    checkForSelectedVoucher() {
      try {
        const selectedVoucher = localStorage.getItem('ramyeon_selected_voucher');
        if (selectedVoucher) {
          const voucher = JSON.parse(selectedVoucher);
          
          this.appliedPromotion = voucher;
          this.promotionDiscount = this.computePromotionDiscount(voucher);
          this.promoCode = voucher.code || voucher.name || '';
          
          this.showSuccessNotification(
            `Voucher "${this.getVoucherDisplayName(voucher)}" applied from profile!`
          );
          
          localStorage.removeItem('ramyeon_selected_voucher');
        }
      } catch (error) {
        console.error('Error applying selected voucher:', error);
        localStorage.removeItem('ramyeon_selected_voucher');
      }
    },

    getVoucherDisplayName(promotion) {
      if (!promotion) return 'Discount Applied';
      
      if (promotion.title) {
        return promotion.title;
      } else if (promotion.name) {
        return promotion.name;
      } else if (promotion.code) {
        return promotion.code.toUpperCase();
      }
      
      return 'Applied Voucher';
    },

    getPromotionDescription(promotion) {
      if (!promotion) return '';
      
      if (promotion.subtitle) {
        return promotion.subtitle;
      }
      
      if (promotion.type === 'percentage') {
        return `${promotion.discount_value}% off on eligible items`;
      } else if (promotion.type === 'fixed_amount') {
        return `₱${promotion.discount_value} off on your order`;
      } else if (promotion.type === 'buy_x_get_y') {
        const config = promotion.discount_config || {};
        return `Buy ${config.buy_quantity || 2} get ${config.get_quantity || 1} free`;
      }
      
      return promotion.description || 'Discount applied to your order';
    },

    // Promotion discount at order level
    computePromotionDiscount(promotion) {
      if (!promotion || !Array.isArray(this.cartItems) || this.cartItems.length === 0) {
        return 0;
      }
      
      let total = 0;
      
      // Profile vouchers
      if (promotion.discount_value !== undefined && promotion.type === undefined) {
        return Math.min(promotion.discount_value, this.subtotal);
      }
      
      if (promotion.discount && typeof promotion.discount === 'string' && promotion.discount.includes('%')) {
        const percentage = parseFloat(promotion.discount.replace('%', ''));
        const discount = (this.subtotal * percentage / 100);
        return Math.min(discount, this.subtotal);
      }
      
      if (promotion.discount && typeof promotion.discount === 'number') {
        return Math.min(promotion.discount, this.subtotal);
      }
      
      // Backend promotions
      for (const item of this.cartItems) {
        total += this.getItemDiscountForPromotion(item, promotion);
      }
      return Math.max(0, total);
    },

    getItemDiscountForPromotion(item, promotion) {
      if (!promotion) return 0;
      if (!this.isItemEligibleForPromotion(item, promotion)) {
        return 0;
      }

      const originalPrice = parseFloat(item.price);
      const quantity = parseInt(item.quantity) || 1;
      let discountAmount = 0;

      // Profile voucher (fixed total) - prorate
      if (promotion.discount_value !== undefined && promotion.type === undefined) {
        const itemRatio = (originalPrice * quantity) / this.subtotal;
        discountAmount = promotion.discount_value * itemRatio;
      } else if (
        promotion.discount &&
        typeof promotion.discount === 'string' &&
        promotion.discount.includes('%')
      ) {
        const percentage = parseFloat(promotion.discount.replace('%', ''));
        discountAmount = (originalPrice * (percentage / 100)) * quantity;
      } else if (promotion.discount && typeof promotion.discount === 'number') {
        const itemRatio = (originalPrice * quantity) / this.subtotal;
        discountAmount = promotion.discount * itemRatio;
      } else if (promotion.type === 'percentage') {
        discountAmount = (originalPrice * (promotion.discount_value / 100)) * quantity;
      } else if (promotion.type === 'fixed_amount') {
        discountAmount = Math.min(promotion.discount_value, originalPrice) * quantity;
      }
      return Math.max(0, discountAmount);
    },

    async increaseQuantity(itemId) {
      const item = this.cartItems.find(item => item.id === itemId);
      if (item) {
        await this.updateCartItemQuantity(item.product_id || item.id, item.quantity + 1);
        await this.recalculateExistingPromotions();

        localStorage.setItem('ramyeon_cart', JSON.stringify(this.cartItems));
        window.dispatchEvent(new Event('cart-updated'));
      }
    },
    
    async decreaseQuantity(itemId) {
      const item = this.cartItems.find(item => item.id === itemId);
      if (item && item.quantity > 1) {
        await this.updateCartItemQuantity(item.product_id || item.id, item.quantity - 1);
        await this.recalculateExistingPromotions();

        localStorage.setItem('ramyeon_cart', JSON.stringify(this.cartItems));
        window.dispatchEvent(new Event('cart-updated'));
      }
    },
    
    async removeItem(itemId) {
      const item = this.cartItems.find(item => item.id === itemId);
      if (item) {
        await this.removeFromCart(item.product_id || item.id);
        await this.recalculateExistingPromotions();

        localStorage.setItem('ramyeon_cart', JSON.stringify(this.cartItems));
        window.dispatchEvent(new Event('cart-updated'));
      }
    },
    
    savePendingOrder(orderId, finalTotal) {
      try {
        const payload = {
          orderId,
          total: Number(finalTotal || this.finalTotal).toFixed(2),
          paymentMethod: this.paymentMethod,
          deliveryType: this.deliveryType,
          pointsEarned:
            this.pointsDiscount > 0 ? 0 : (this.confirmedOrder?.pointsEarned || 0),
          pointsUsed: this.useLoyaltyPoints ? (parseInt(this.pointsToRedeem) || 0) : 0,
          timestamp: Date.now()
        };
        localStorage.setItem(this.pendingOrderKey, JSON.stringify(payload));
        this.pendingOrder = payload;
        if (CART_DEBUG) console.log('📝 Pending order saved:', payload);
      } catch (error) {
        console.error('❌ Failed to save pending order:', error);
      }
    },

    loadPendingOrder(orderId = null) {
      try {
        const raw = localStorage.getItem(this.pendingOrderKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (orderId && parsed.orderId !== orderId) {
          return null;
        }
        return parsed;
      } catch (error) {
        console.error('❌ Failed to load pending order:', error);
        return null;
      }
    },

    clearPendingOrder() {
      localStorage.removeItem(this.pendingOrderKey);
      this.pendingOrder = null;
    },

    handleCartImageError(event) {
      event.target.src = this.placeholderImage;
    },
    
    // Loyalty Points Methods
    async onPointsToggle() {
      if (CART_DEBUG) console.log('🎯 Points toggle changed:', this.useLoyaltyPoints);
      
      if (this.useLoyaltyPoints) {
        const userPoints = this.userProfile?.loyalty_points || this.loyaltyBalance || 0;
        
        const maxDiscount = Math.min(20, this.subtotal * 0.20);
        const maxPointsFromDiscount = Math.floor(maxDiscount * 4);
        
        this.maxPointsToRedeem = Math.min(
          userPoints,
          maxPointsFromDiscount,
          80
        );
        
        this.pointsToRedeem = '';
        this.pointsDiscount = 0;
      } else {
        this.pointsToRedeem = '';
        this.pointsDiscount = 0;
        this.maxPointsToRedeem = 0;
        this.pointsApplied = false;
        this.removeLoyaltyPoints();
        if (CART_DEBUG) console.log('❌ Loyalty points disabled');
      }
    },
    
    handlePointsInput(event) {
      const value = event.target.value.replace(/[^0-9]/g, '');
      this.pointsToRedeem = value;
      
      if (CART_DEBUG) console.log('⌨️ User typing:', value);
      
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 40) {
        this.pointsApplied = true;
        this.updatePointsDiscount();
      } else if (value === '' || numValue === 0) {
        this.pointsDiscount = 0;
        this.pointsApplied = false;
      }
    },
    
    setPoints(amount) {
      this.pointsToRedeem = parseInt(amount);
      if (CART_DEBUG) console.log('🔘 Set points to:', this.pointsToRedeem);
      this.validatePointsInput();
    },
    
    validatePointsInput() {
      if (
        this.pointsToRedeem === '' ||
        this.pointsToRedeem === null ||
        this.pointsToRedeem === undefined
      ) {
        if (!this.pointsApplied || this.pointsDiscount === 0) {
          this.pointsDiscount = 0;
        }
        return;
      }
      
      let points = parseInt(this.pointsToRedeem);
      
      if (isNaN(points)) {
        this.pointsToRedeem = '';
        this.pointsDiscount = 0;
        return;
      }
      
      if (points < 0) {
        points = Math.abs(points);
      }
      
      if (points === 0) {
        this.pointsToRedeem = '';
        this.pointsDiscount = 0;
        return;
      }
      
      if (points < 40) {
        points = 40;
      }
      
      if (points > this.maxPointsToRedeem) {
        points = this.maxPointsToRedeem;
      }
      
      this.pointsToRedeem = points;
      this.pointsApplied = true;
      
      this.updatePointsDiscount();
    },
    
    async updatePointsDiscount() {
      
      if (
        this.pointsApplied &&
        this.pointsDiscount > 0 &&
        (!this.pointsToRedeem || this.pointsToRedeem === '')
      ) {
        return;
      }
      
      const points = parseInt(this.pointsToRedeem) || 0;
      
      if (!this.useLoyaltyPoints || points === 0) {
        this.pointsDiscount = 0;
        this.pointsApplied = false;
        return;
      }
      
      try {
        const result = this.calculatePointsDiscount(points);
        
        if (CART_DEBUG) console.log('💎 Discount calculation result:', result);
        
        if (result && result.discount_amount !== undefined) {
          this.pointsDiscount = result.discount_amount;
          
          const userPoints = this.userProfile?.loyalty_points || this.loyaltyBalance || 0;
          const maxDiscount = Math.min(20, this.subtotal * 0.20);
          this.maxPointsToRedeem = Math.min(
            userPoints,
            Math.floor(maxDiscount * 4),
            80
          );
          
          if (points > this.maxPointsToRedeem) {
            this.pointsToRedeem = this.maxPointsToRedeem;
          }
        }
      } catch (error) {
        console.error('❌ Points calculation error:', error);
        this.pointsDiscount = 0;
      }
    },
    
    async applyPromoCode() {
      if (!this.promoCode || this.promoCode.trim() === '') {
        return;
      }
      
      this.applyingPromo = true;
      this.promoError = null;
      
      try {
        if (CART_DEBUG) console.log('🎁 Applying promo code:', this.promoCode);
        
        const result = await this.applyPromotionToCart(this.promoCode.trim());
        
        if (result.success) {
          this.appliedPromotion = result.data.promotion;
          this.promotionDiscount = result.data.discount_amount;
          this.promoError = null;
          
          this.showSuccessNotification(
            `Promo code applied! You saved ₱${this.promotionDiscount.toFixed(2)}`
          );
        } else {
          this.promoError =
            result.error || 'Invalid promo code. Please check and try again.';
        }
      } catch (error) {
        console.error('❌ Error applying promo code:', error);
        this.promoError = error.message || 'Failed to apply promo code. Please try again.';
      } finally {
        this.applyingPromo = false;
      }
    },
    
    async removePromoCode() {
      if (CART_DEBUG) console.log('🗑️ Removing promo code');
      
      if (this.appliedPromotion) {
        await this.removePromotionFromCart(this.appliedPromotion.id);
      }
      
      this.appliedPromotion = null;
      this.promotionDiscount = 0;
      this.promoCode = '';
      this.promoError = null;
      
      this.showSuccessNotification('Promo code removed');
    },

    // Automatic promotion application with voucher priority
    async autoApplyBestPromotion() {
      try {
        // 1. PRIORITY: If user selected a voucher via store → use it and STOP
        if (this.cartStore?.selectedVoucher) {
          const voucher = this.cartStore.selectedVoucher;

          this.appliedPromotion = voucher;
          this.promotionDiscount = this.computePromotionDiscount(voucher);

          return;
        }

        // 2. If a manual promo code / voucher already applied, don't override
        if (this.appliedPromotion) {
          return;
        }

        // 3. No active promotions or empty cart → nothing to auto-apply
        if (!this.activePromotions || this.activePromotions.length === 0) {
          if (CART_DEBUG) console.log('⚠️ No active promotions available');
          return;
        }
        if (!this.cartItems || this.cartItems.length === 0) {
          if (CART_DEBUG) console.log('⚠️ Cart is empty');
          return;
        }

        // Filter out PWD & SENIOR
        const filteredPromotions = this.activePromotions.filter(promo =>
          promo.name !== 'PWD' &&
          promo.name !== 'Senior Citizen'
        );

        let bestPromotion = null;
        let bestDiscount = 0;

        // 4. Compute the best promotion from the filtered list
        for (const promo of filteredPromotions) {
          if (promo.status !== 'active') continue;

          const discount = this.computePromotionDiscount(promo);

          if (discount > bestDiscount) {
            bestDiscount = discount;
            bestPromotion = promo;
          }
        }

        // 5. Apply the best promotion if it exists
        if (bestPromotion && bestDiscount > 0) {
          this.appliedPromotion = bestPromotion;
          this.promotionDiscount = bestDiscount;
        } else {
          this.appliedPromotion = null;
          this.promotionDiscount = 0;
          if (CART_DEBUG) console.log('⚠️ No applicable promotions found');
        }

      } catch (e) {
        console.error('Auto-apply promotion error:', e);
      }
    },
    
    async recalculateExistingPromotions() {
      try {
        if (!this.appliedPromotion || !this.cartItems.length) return;

        if (
          this.appliedPromotion.name === 'PWD' ||
          this.appliedPromotion.name === 'Senior Citizen'
        ) {
          this.appliedPromotion = null;
          this.promotionDiscount = 0;
          return;
        }

        const newDiscount = this.computePromotionDiscount(this.appliedPromotion);

        this.promotionDiscount = newDiscount;

        if (newDiscount === 0) {
          this.appliedPromotion = null;
          this.promotionDiscount = 0;
        }
      } catch (error) {
        console.error('Error recalculating promotions:', error);
      }
    },
    
    // Per-item discount calculation
    getItemDiscount(item) {
      
      const applicablePromotion = this.getApplicablePromotionForItem(item);
      
      if (!applicablePromotion) {
        return 0;
      }
      
      const originalPrice = parseFloat(item.price);
      let discountAmount = 0;
      
      if (applicablePromotion.type === 'percentage') {
        discountAmount = originalPrice * (applicablePromotion.discount_value / 100);
      } else if (applicablePromotion.type === 'fixed_amount') {
        discountAmount = Math.min(applicablePromotion.discount_value, originalPrice);
      }
      
      return Math.max(0, discountAmount);
    },
    
    getItemDiscountedPrice(item) {
      const originalPrice = parseFloat(item.price);
      const discount = this.getItemDiscount(item);
      return Math.max(0, originalPrice - discount);
    },
    
    getItemDiscountText(item) {
      const applicablePromotion = this.getApplicablePromotionForItem(item);
      if (!applicablePromotion) return '';
      
      if (applicablePromotion.type === 'percentage') {
        return `${applicablePromotion.discount_value}% OFF`;
      } else if (applicablePromotion.type === 'fixed_amount') {
        return `₱${applicablePromotion.discount_value} OFF`;
      }
      
      return 'DISCOUNT';
    },
    
    // 🔑 Prefer globalPromotions first (like Drinks Promo) then fallback to others
    getApplicablePromotionForItem(item) {
      const globals = this.globalPromotions || [];
      const actives = this.activePromotions || [];

      // Avoid duplicates by ID
      const globalIds = new Set(
        globals.map(p => p.id || p.promotion_id)
      );

      const orderedPromotions = [
        ...globals,
        ...actives.filter(p => !globalIds.has(p.id || p.promotion_id))
      ];

      for (const promotion of orderedPromotions) {
        if (this.isItemEligibleForPromotion(item, promotion)) {
          return promotion;
        }
      }

      return null;
    },
    
    isItemEligibleForPromotion(item, promotion) {
      if (!promotion) return false;
      
      if (promotion.status !== 'active') return false;
      if (promotion.isDeleted) return false;
      
      const now = new Date();
      const startDate = promotion.start_date ? new Date(promotion.start_date) : null;
      const endDate = promotion.end_date ? new Date(promotion.end_date) : null;
      
      if (startDate && now < startDate) return false;
      if (endDate && now > endDate) return false;
      
      const targetType = (promotion.target_type || '').toLowerCase();
      
      if (targetType === 'all') {
        return true;
      } else if (targetType === 'category' || targetType === 'categories') {
        const isCategoryMatch =
          promotion.target_ids && promotion.target_ids.includes(item.category_id);
        
        // Fallback for "Drinks"-like promos if using name logic
        if (!isCategoryMatch && promotion.name &&
            promotion.name.toLowerCase().includes('drinks')) {
          const itemName = item.name.toLowerCase();
          const itemDescription = (item.description || '').toLowerCase();
          const drinksKeywords = [
            'drink', 'beverage', 'juice', 'soda', 'water',
            'tea', 'coffee', 'milk', '7 up', 'coke', 'pepsi', 'alaska'
          ];
          return drinksKeywords.some(keyword =>
            itemName.includes(keyword) || itemDescription.includes(keyword)
          );
        }
        
        return isCategoryMatch;
      } else if (targetType === 'product' || targetType === 'products') {
        const isProductMatch =
          promotion.target_ids &&
          promotion.target_ids.includes(item.product_id || item.id);
        return isProductMatch;
      } else if (promotion.target_type === 'specific') {
        const itemName = item.name.toLowerCase();
        const itemDescription = (item.description || '').toLowerCase();
        
        if (promotion.name && promotion.name.toLowerCase().includes('drinks')) {
          const drinksKeywords = [
            'drink', 'beverage', 'juice', 'soda', 'water',
            'tea', 'coffee', 'milk', '7 up', 'coke', 'pepsi', 'alaska'
          ];
          return drinksKeywords.some(keyword =>
            itemName.includes(keyword) || itemDescription.includes(keyword)
          );
        }
        
        if (promotion.name && promotion.name.toLowerCase().includes('ramyeon')) {
          const ramyeonKeywords = ['ramyeon', 'ramen', 'noodle', 'soup'];
          return ramyeonKeywords.some(keyword =>
            itemName.includes(keyword) || itemDescription.includes(keyword)
          );
        }
      }
      
      return false;
    },
    
    showSuccessNotification(message) {
      const notification = document.createElement('div');
      notification.className = 'promo-notification success';
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;
        padding: 15px 25px;
        border-radius: 12px;
        box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
        z-index: 10000;
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        animation: slideInRight 0.3s ease-out;
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.animation = 'slideInRight 0.3s ease-in reverse';
        setTimeout(() => {
          if (notification.parentNode) {
            document.body.removeChild(notification);
          }
        }, 300);
      }, 3000);
    },
    
    // Map and location methods
    async openMap() {
      this.showMap = true;
      
      if (!window.google) {
        try {
          await this.loadGoogleMapsScript();
        } catch (error) {
          console.error('Failed to load Google Maps:', error);
          alert(
            'Unable to load Google Maps. Please check:\n' +
            '1. Internet connection\n' +
            '2. API key is valid\n' +
            '3. Billing is enabled in Google Cloud Console\n\n' +
            'You can manually enter your address instead.'
          );
          this.closeMap();
          return;
        }
      }
      
      this.$nextTick(() => {
        this.initializeMap();
      });
    },

    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_KEY}&libraries=places&callback=initMap`;
        script.async = true;
        script.defer = true;
        
        window.initMap = () => {
          resolve();
          delete window.initMap;
        };
        
        script.onerror = () => {
          reject(new Error('Failed to load Google Maps script'));
        };
        
        document.head.appendChild(script);
      });
    },

    closeMap() {
      this.showMap = false;
    },

    initializeMap() {
      const mapElement = document.getElementById('google-map');
      if (!mapElement || !window.google) {
        console.error('Google Maps not loaded or map element not found');
        alert('Unable to load Google Maps. Please check your internet connection and try again.');
        this.closeMap();
        return;
      }

      try {
        const defaultCenter = { lat: 8.1837, lng: 126.3162 };
        
        this.map = new window.google.maps.Map(mapElement, {
          center: defaultCenter,
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          mapId: 'DEMO_MAP_ID'
        });

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            position => {
              const userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              this.map.setCenter(userLocation);
              this.placeMarker(userLocation);
            },
            () => {
              this.placeMarker(defaultCenter);
            }
          );
        } else {
          this.placeMarker(defaultCenter);
        }

        this.map.addListener('click', event => {
          this.placeMarker(event.latLng);
        });
      } catch (error) {
        console.error('Map initialization error:', error);
        
        if (error.message && error.message.includes('Billing')) {
          alert(
            '⚠️ Google Maps API Error: Billing is not enabled.\n\n' +
            'Please enable billing for this API key in Google Cloud Console:\n' +
            '1. Go to console.cloud.google.com\n' +
            '2. Select your project\n' +
            '3. Enable billing\n' +
            '4. Enable Maps JavaScript API'
          );
        } else {
          alert('Unable to initialize Google Maps. Error: ' + error.message);
        }
        this.closeMap();
      }
    },

    placeMarker(location) {
      if (this.marker) {
        this.marker.setMap(null);
      }

      this.marker = new window.google.maps.Marker({
        position: location,
        map: this.map,
        animation: window.google.maps.Animation.DROP,
        title: 'Delivery Location'
      });

      this.getAddressFromCoordinates(location);
    },

    getAddressFromCoordinates(location) {
      const geocoder = new window.google.maps.Geocoder();
      const latLng = {
        lat: typeof location.lat === 'function' ? location.lat() : location.lat,
        lng: typeof location.lng === 'function' ? location.lng() : location.lng
      };

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.deliveryAddress = results[0].formatted_address;
          
          if (this.marker) {
            const infoWindow = new window.google.maps.InfoWindow({
              content: `<div style="padding: 10px;">
                <strong>Selected Location:</strong><br/>
                ${results[0].formatted_address}
              </div>`
            });
            infoWindow.open(this.map, this.marker);
          }
        } else {
          console.error('Geocoder failed:', status);
          alert('Unable to get address for this location. Please try another location.');
        }
      });
    },

    // Payment and checkout methods
    async proceedToCheckout() {
      if (!this.canCheckout) {
        return;
      }

      if (this.isProcessing) {
        return;
      }

      try {
        this.isProcessing = true;

        if (!this.userProfile || !this.userProfile.id) {
          throw new Error('Please log in to place an order');
        }

        const orderItems = this.cartItems.map(item => ({
          product_id: item.product_id || item.id,
          quantity: item.quantity || 1,
          price: this.getItemDiscountedPrice(item),
          product_name: item.name || item.product_name,
          original_price: item.price
        }));

        const subtotal = this.subtotal;
        const deliveryFee = this.deliveryType === 'delivery' ? this.deliveryFee : 0;
        const serviceFee = this.serviceFee;
        const totalDiscount =
          (this.pointsDiscount || 0) +
          (this.promotionDiscount || 0);
        const finalTotal = this.finalTotal;

        const orderData = {
          user: {
            id: this.userProfile.id,
            email: this.userProfile.email,
            full_name: this.userProfile.full_name
          },
          customer_id: this.userProfile.id,
          items: orderItems,
          delivery_address: this.deliveryType === 'delivery'
            ? { address: this.deliveryAddress, type: 'delivery' }
            : {},
          delivery_type: this.deliveryType,
          payment_method: this.paymentMethod,
          subtotal,
          delivery_fee: deliveryFee,
          service_fee: serviceFee,
          discount: totalDiscount,
          total_amount: finalTotal,
          points_to_redeem: this.useLoyaltyPoints ? parseInt(this.pointsToRedeem) || 0 : 0,
          loyalty_points: this.useLoyaltyPoints ? parseInt(this.pointsToRedeem) || 0 : 0,
          special_instructions: this.specialInstructions || '',
          notes: this.specialInstructions || '',
          promotion_id: this.appliedPromotion?.id || this.appliedPromotion?._id || null
        };

        console.log('📦 [Cart] Order data prepared:', JSON.stringify(orderData, null, 2));
        console.log('📦 [Cart] customer_id:', orderData.customer_id);
        console.log('📦 [Cart] items count:', orderData.items.length);
        console.log('📦 [Cart] payment_method:', orderData.payment_method);
        console.log('📦 [Cart] delivery_type:', orderData.delivery_type);
        console.log('📦 [Cart] total_amount:', orderData.total_amount);
        console.log('📦 [Cart] points_to_redeem:', orderData.points_to_redeem);

        if (this.paymentMethod === 'cash') {
          const result = await this.createOrder(orderData);
          console.log('📥 [Cart] createOrder result (cash):', JSON.stringify(result, null, 2));

          if (result.success) {
            const orderId = result.data.id || result.data.order_id || result.data.transaction_id;
            console.log('✅ [Cart] Order placed — orderId:', orderId);
            this.confirmedOrder = {
              id: orderId,
              total: finalTotal.toFixed(2),
              paymentMethod: this.paymentMethod,
              deliveryType: this.deliveryType,
              pointsEarned: result.data.order?.loyalty?.points_earned || 0,
              pointsUsed: orderData.points_to_redeem || 0
            };
            window.dispatchEvent(new CustomEvent('order-placed', { detail: { orderId } }));
            this.clearCart();
            localStorage.removeItem('ramyeon_cart');
            this.$emit('cartCleared');
            this.showOrderConfirmation = true;
          } else {
            throw new Error(result.error || 'Failed to create order');
          }
        } else if (this.paymentMethod === 'gcash') {
          const result = await this.createOrder(orderData);
          console.log('📥 [Cart] createOrder result (gcash):', JSON.stringify(result, null, 2));

          if (result.success) {
            const orderId =
              result.data?.order_id ||
              result.data?.order?.order_id ||
              result.data?.id ||
              result.data?.transaction_id ||
              result.data?.order?._id;
            console.log('✅ [Cart] Order placed (gcash) — orderId:', orderId);

            window.dispatchEvent(new CustomEvent('order-placed', { detail: { orderId } }));
            this.savePendingOrder(orderId, finalTotal);
            const paymentSource = await this.processGCashPayment(orderId);

            if (paymentSource && paymentSource.checkout_url) {
              window.location.href = paymentSource.checkout_url;
            } else {
              throw new Error('Failed to initialize GCash payment');
            }
          } else {
            throw new Error(result.error || 'Failed to create order');
          }
        } else if (this.paymentMethod === 'card') {
          const result = await this.createOrder(orderData);
          console.log('📥 [Cart] createOrder result (card):', JSON.stringify(result, null, 2));

          if (result.success) {
            const orderId =
              result.data?.order_id ||
              result.data?.order?.order_id ||
              result.data?.id ||
              result.data?.transaction_id ||
              result.data?.order?._id;
            console.log('✅ [Cart] Order placed (card) — orderId:', orderId);

            window.dispatchEvent(new CustomEvent('order-placed', { detail: { orderId } }));
            this.savePendingOrder(orderId, finalTotal);
            const paymentSource = await this.processCardPayment(orderId);

            if (paymentSource && paymentSource.checkout_url) {
              window.location.href = paymentSource.checkout_url;
            } else {
              throw new Error('Failed to initialize card payment');
            }
          } else {
            throw new Error(result.error || 'Failed to create order');
          }
        } else if (this.paymentMethod === 'grabpay') {
          const result = await this.createOrder(orderData);
          console.log('📥 [Cart] createOrder result (grabpay):', JSON.stringify(result, null, 2));

          if (result.success) {
            const orderId =
              result.data?.order_id ||
              result.data?.order?.order_id ||
              result.data?.id ||
              result.data?.transaction_id ||
              result.data?.order?._id;
            console.log('✅ [Cart] Order placed (grabpay) — orderId:', orderId);

            window.dispatchEvent(new CustomEvent('order-placed', { detail: { orderId } }));
            this.savePendingOrder(orderId, finalTotal);
            const paymentSource = await this.processGrabPayPayment(orderId);

            if (paymentSource && paymentSource.checkout_url) {
              window.location.href = paymentSource.checkout_url;
            } else {
              throw new Error('Failed to initialize GrabPay payment');
            }
          } else {
            throw new Error(result.error || 'Failed to create order');
          }
        } else {
          const result = await this.createOrder(orderData);

          if (result.success) {
            const orderId = result.data.id || result.data.order_id || result.data.order?.order_id;
            this.confirmedOrder = {
              id: orderId,
              total: finalTotal.toFixed(2),
              paymentMethod: this.paymentMethod,
              deliveryType: this.deliveryType,
              pointsEarned: result.data.order?.loyalty?.points_earned || 0,
              pointsUsed: orderData.points_to_redeem || 0
            };
            window.dispatchEvent(new CustomEvent('order-placed', { detail: { orderId } }));
            this.clearCart();
            localStorage.removeItem('ramyeon_cart');
            this.showOrderConfirmation = true;
          } else {
            throw new Error(result.error || 'Failed to create order');
          }
        }
      } catch (error) {
        console.error('❌ Checkout error:', error);
        alert(error.message || 'Failed to place order. Please try again.');
      } finally {
        this.isProcessing = false;
      }
    },

    async processGCashPayment(orderId) {
      try {
        const source = await paymongoAPI.processGCashPayment({
          amount: this.finalTotal,
          orderId,
          customerEmail: this.userProfile?.email || 'customer@example.com',
          customerName: this.userProfile?.full_name || 'Customer'
        });
        
        const checkoutUrl =
          source?.data?.attributes?.redirect?.checkout_url ||
          source?.data?.attributes?.checkout_url ||
          source?.checkout_url;
        
        if (!checkoutUrl) {
          console.error('GCash payment response:', source);
          throw new Error('No checkout URL received from GCash payment');
        }
        
        return {
          ...source,
          checkout_url: checkoutUrl
        };
      } catch (error) {
        console.error('GCash payment error:', error);
        throw new Error('Failed to process GCash payment. Please try again.');
      }
    },

    async processCardPayment(orderId) {
      try {
        const source = await paymongoAPI.processCardPayment({
          amount: this.finalTotal,
          orderId,
          customerEmail: this.userProfile?.email || 'customer@example.com',
          customerName: this.userProfile?.full_name || 'Customer',
          cardDetails: this.cardDetails
        });
        
        const checkoutUrl =
          source?.data?.attributes?.checkout_url ||
          source?.data?.attributes?.redirect?.checkout_url ||
          source?.checkout_url;
        
        if (!checkoutUrl) {
          console.error('Card payment response:', source);
          throw new Error('No checkout URL received from card payment');
        }
        
        return {
          ...source,
          checkout_url: checkoutUrl
        };
      } catch (error) {
        console.error('Card payment error:', error);
        throw new Error('Failed to process card payment. Please try again.');
      }
    },

    async processGrabPayPayment(orderId) {
      try {
        const source = await paymongoAPI.processGrabPayPayment({
          amount: this.finalTotal,
          orderId,
          customerEmail: this.userProfile?.email || 'customer@example.com',
          customerName: this.userProfile?.full_name || 'Customer'
        });
        
        const checkoutUrl =
          source?.data?.attributes?.redirect?.checkout_url ||
          source?.data?.attributes?.checkout_url ||
          source?.checkout_url;
        
        if (!checkoutUrl) {
          console.error('GrabPay payment response:', source);
          throw new Error('No checkout URL received from GrabPay payment');
        }
        
        return {
          ...source,
          checkout_url: checkoutUrl
        };
      } catch (error) {
        console.error('GrabPay payment error:', error);
        throw new Error('Failed to process GrabPay payment. Please try again.');
      }
    },
    
    checkPaymentReturn() {
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      
      const paymentStatus = urlParams.get('payment') || hashParams.get('payment');
      const orderId =
        urlParams.get('order') ||
        hashParams.get('order') ||
        urlParams.get('order_id') ||
        hashParams.get('order_id');
      
      if (paymentStatus && orderId) {
        const pendingOrder = this.loadPendingOrder(orderId);
        
        if (paymentStatus === 'success') {
          
          this.confirmedOrder = {
            id: orderId,
            total: pendingOrder?.total || this.finalTotal.toFixed(2),
            paymentMethod: pendingOrder?.paymentMethod || this.paymentMethod,
            deliveryType: pendingOrder?.deliveryType || this.deliveryType,
            pointsEarned: pendingOrder?.pointsEarned || 0,
            pointsUsed: pendingOrder?.pointsUsed || 0
          };
          
          this.clearCart();
          localStorage.removeItem('ramyeon_cart');
          this.$emit('cartCleared');
          this.showOrderConfirmation = true;
        } else {
          alert('Payment failed or was cancelled. Please try again.');
        }
        
        this.clearPendingOrder();
      }
    },
    
    closeConfirmationModal() {
      this.showOrderConfirmation = false;
      
      window.location.hash = '#/cart';
    },
    
    goToHome() {
      this.showOrderConfirmation = false;
      
      window.location.hash = '#/';
      this.$emit('setCurrentPage', 'Home');
    },
    
    async checkActiveOrder() {
      this.checkingActiveOrder = true;
      try {
        const result = await ordersAPI.getAll(10);
        const orders = result?.results || [];
        const activeStatuses = new Set(['pending', 'confirmed', 'processing', 'preparing', 'on_the_way']);
        const found = orders.find(o => activeStatuses.has(o?.order?.status));
        this.activeOrder = found
          ? { order_id: found.transaction_id, status: found.order?.status }
          : null;
      } catch (err) {
        console.error('[Cart] checkActiveOrder failed:', err);
        this.activeOrder = null;
      } finally {
        this.checkingActiveOrder = false;
      }
    },

    async loadUserProfile() {
      try {
        this.isUserLoading = true;

        const response = await authAPI.getProfile();
        
        const customer = 
          response.customer ||
          response.data ||
          response;
        
        if (!customer) {
          return;
        }

          this.userProfile = {
          id: customer.id || customer.customer_id || null,
          email: customer.email || '',
          full_name: customer.full_name || customer.name || '',
          loyalty_points: customer.loyalty_points || 0,
          phone: customer.phone || '',
          deliveryAddress: customer.delivery_address || {}
        };


      } catch (err) {
        console.error('❌ Error loading user profile:', err);
        this.userProfile = null;
      } finally {
        this.isUserLoading = false;
      }
    }
  },

  async mounted() {

    const hasPaymentParams =
      window.location.hash.includes('payment=') ||
      window.location.search.includes('payment=');
    
    if (hasPaymentParams) {
      setTimeout(() => {
        this.checkPaymentReturn();
      }, 100);
    } else {
      if (CART_DEBUG) {
        console.log('[Cart] No payment params in URL, skipping payment return check');
      }
      if (localStorage.getItem(this.pendingOrderKey)) {
        if (CART_DEBUG) console.log('[Cart] Clearing stale pending order');
        this.clearPendingOrder();
      }
    }
    
    await this.loadUserProfile();
    await this.checkActiveOrder();

    // Check for voucher or loyalty intent from Profile before loading cart
    this.checkForSelectedVoucher();
    this.checkForLoyaltyIntent();

    const savedCart = localStorage.getItem('ramyeon_cart');
    if (CART_DEBUG) {
      console.log('[Cart] Loading cart from localStorage:', savedCart ? 'Found' : 'Empty');
    }
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (CART_DEBUG) console.log('[Cart] Parsed cart items:', parsedCart.length);
        
        if (Array.isArray(parsedCart)) {
          this.cartItems = parsedCart;
          if (CART_DEBUG) {
            console.log('[Cart] Cart loaded:', this.cartItems.length, 'items');
          }
        } else {
          localStorage.removeItem('ramyeon_cart');
          this.cartItems = [];
        }
      } catch (error) {
        console.error('❌ Error loading cart:', error);
        localStorage.removeItem('ramyeon_cart');
        this.cartItems = [];
      }
    } else {
      this.cartItems = [];
    }
    
    if (this.cartItems.length > 0 && this.calculateCartTotals) {
      await this.calculateCartTotals();
    }
    
    this.$nextTick(() => {
      this.$forceUpdate();
    });
    
    try {
      await this.getActivePromotions();
      
      // Build globalPromotions from activePromotions
      this.loadGlobalPromotions();
      
      // Auto-apply best promotion if no voucher (e.g. Drinks Promo)
      await this.autoApplyBestPromotion();
      
    } catch (error) {
      console.error('❌ Error loading promotions:', error);
    }
  }
};
</script>


<style scoped>
.cart-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  font-family: 'Poppins', sans-serif;
}

.cart-container {
  max-width: 1200px;
  margin: 0 auto;
}

.cart-header {
  text-align: center;
  margin-bottom: 30px;
}

.cart-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cart-header p {
  color: #666;
  font-size: 1.1rem;
}

.cart-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  align-items: start;
}

.cart-items {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.cart-item {
  display: grid;
  grid-template-columns: 80px 1fr auto auto auto;
  gap: 15px;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
}

.cart-item:last-child {
  border-bottom: none;
}

.item-image {
  width: 80px;
  height: 80px;
  border-radius: 15px;
  object-fit: cover;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.item-details h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.item-description {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.item-price {
  font-weight: 600;
  color: #ff4757;
  font-size: 1.1rem;
}

.item-price-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.item-price.has-discount {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  font-size: 0.9rem;
  font-weight: 500;
}

.current-price {
  color: #ff4757;
  font-weight: 700;
  font-size: 1.2rem;
}

.item-discount-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.discount-badge {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.savings {
  color: #28a745;
  font-size: 0.8rem;
  font-weight: 600;
}

.total-with-discount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

.original-total {
  text-decoration: line-through;
  color: #999;
  font-size: 0.9rem;
  font-weight: 500;
}

.discounted-total {
  color: #ff4757;
  font-weight: 700;
  font-size: 1.2rem;
}

.regular-total {
  font-weight: 700;
  font-size: 1.2rem;
  color: #333;
}

.item-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8f9fa;
  border-radius: 25px;
  padding: 5px;
}

.quantity-btn {
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 50%;
  background: #ff4757;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.quantity-btn:hover {
  background: #ff3742;
  transform: scale(1.1);
}

.quantity {
  font-weight: 600;
  font-size: 1.1rem;
  min-width: 30px;
  text-align: center;
}

.item-total {
  font-weight: 700;
  font-size: 1.2rem;
  color: #333;
}

.remove-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: #dc3545;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.order-summary {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  position: sticky;
  top: 20px;
}

.order-summary h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 1rem;
}

.summary-row.total {
  border-top: 2px solid #eee;
  padding-top: 15px;
  font-weight: 700;
  font-size: 1.2rem;
  color: #ff4757;
}

.summary-row.discount-row {
  color: #28a745;
  font-weight: 600;
  font-size: 1.05rem;
}

.discount-amount {
  color: #28a745;
  font-weight: 700;
}

/* Promo Code Section */
.promo-code-section {
  margin: 20px 0;
  padding: 15px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.promo-input-group {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.promo-input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid #eee;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
  text-transform: uppercase;
}

.promo-input:focus {
  outline: none;
  border-color: #ff4757;
  box-shadow: 0 0 0 3px rgba(255, 71, 87, 0.1);
}

.promo-input:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.apply-promo-btn,
.remove-promo-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.apply-promo-btn {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  min-width: 90px;
}

.apply-promo-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #218838, #1aa179);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
}

.apply-promo-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.remove-promo-btn {
  background: #dc3545;
  color: white;
}

.remove-promo-btn:hover {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.3);
}

.loading-spinner-small {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.applied-promo {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  padding: 15px;
  border-radius: 10px;
  margin-top: 10px;
  border: 2px solid #28a745;
}

.promo-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.promo-icon {
  font-size: 1.3rem;
}

.promo-name {
  font-weight: 700;
  color: #2e7d32;
  font-size: 1rem;
}

.promo-discount-info {
  color: #1b5e20;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 30px;
}

.promo-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffebee;
  color: #c62828;
  padding: 10px 15px;
  border-radius: 8px;
  margin-top: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid #ef5350;
}

.error-icon {
  font-size: 1.2rem;
}

/* Loyalty Points Section */
.loyalty-points-section {
  margin: 20px 0;
  padding: 15px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  background: linear-gradient(135deg, #fff8e1, #ffecb3);
  border-radius: 10px;
  padding: 20px;
}

.points-header {
  margin-bottom: 15px;
}

.points-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #f57c00;
}

.points-icon {
  font-size: 1.3rem;
}

.points-label {
  font-size: 1rem;
}

.points-balance {
  font-size: 1.2rem;
  font-weight: 700;
  color: #e65100;
}

.points-value {
  font-size: 0.9rem;
  color: #bf360c;
  font-weight: 500;
}

.points-redemption {
  margin-top: 15px;
}

.points-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-bottom: 15px;
}

.points-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #ff9800;
}

.checkbox-text {
  font-weight: 600;
  color: #e65100;
  font-size: 1rem;
}

.points-input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
}

.points-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.points-input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid #ffb74d;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
  background: white;
  min-width: 120px;
}

.quick-select-points {
  display: flex;
  gap: 6px;
}

.quick-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
}

.quick-btn:hover {
  background: linear-gradient(135deg, #fb8c00, #ef6c00);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(255, 152, 0, 0.3);
}

.quick-btn:active {
  transform: translateY(0);
}

.points-input:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

.points-discount {
  font-weight: 700;
  color: #e65100;
  font-size: 1rem;
  background: #fff3e0;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ffb74d;
}

.summary-row.points-discount-row {
  color: #ff9800;
  font-weight: 600;
  font-size: 1.05rem;
  background: linear-gradient(135deg, #fff8e1, #ffecb3);
  padding: 8px 12px;
  border-radius: 8px;
  margin: 5px 0;
}

.loyalty-points-summary {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  border-radius: 10px;
  padding: 15px;
  margin-top: 15px;
  border: 2px solid #4caf50;
}

.points-used {
  color: #f44336;
  font-weight: 700;
}

.points-earned {
  color: #4caf50;
  font-weight: 700;
}

.points-insufficient {
  margin-top: 10px;
  padding: 10px 15px;
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
}

.insufficient-message {
  color: #e65100;
  font-weight: 600;
  font-size: 0.9rem;
}

.points-rates {
  margin-top: 5px;
  text-align: center;
}

.points-rates small {
  color: #f57c00;
  font-weight: 500;
  font-size: 0.8rem;
}

.delivery-options,
.payment-methods,
.special-instructions {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.delivery-options h2,
.payment-methods h2,
.special-instructions h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.option-label {
  display: block;
  cursor: pointer;
}

.option-content {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border: 2px solid #eee;
  border-radius: 15px;
  transition: all 0.3s ease;
}

.option-label input[type="radio"]:checked + .option-content {
  border-color: #ff4757;
  background: rgba(255, 71, 87, 0.05);
}

.option-icon {
  font-size: 2rem;
}

.option-text h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.option-text p {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.option-time {
  color: #ff4757;
  font-weight: 600;
  font-size: 0.9rem;
}

.delivery-address {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.delivery-address h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.address-input {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.address-field {
  flex: 1;
  padding: 15px 20px;
  border: 2px solid #eee;
  border-radius: 15px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
}

.address-field:focus {
  outline: none;
  border-color: #ff4757;
  box-shadow: 0 0 0 3px rgba(255, 71, 87, 0.1);
}

.map-btn {
  padding: 15px 25px;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.map-btn:hover {
  background: #ff3742;
  transform: translateY(-2px);
}

.map-container {
  position: relative;
}

.map-instructions {
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 15px;
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 3px solid #ff4757;
}

.google-map {
  height: 400px;
  width: 100%;
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.close-map-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  border: none;
  padding: 10px 15px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.payment-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.payment-option {
  display: block;
  cursor: pointer;
}

.payment-content {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border: 2px solid #eee;
  border-radius: 15px;
  transition: all 0.3s ease;
}

.payment-option input[type="radio"]:checked + .payment-content {
  border-color: #ff4757;
  background: rgba(255, 71, 87, 0.05);
}

.payment-icon {
  font-size: 1.5rem;
}

.instructions-field {
  width: 100%;
  min-height: 100px;
  padding: 15px 20px;
  border: 2px solid #eee;
  border-radius: 15px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  resize: vertical;
  transition: all 0.3s ease;
}

.instructions-field:focus {
  outline: none;
  border-color: #ff4757;
  box-shadow: 0 0 0 3px rgba(255, 71, 87, 0.1);
}

.active-order-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff8e1;
  border: 1.5px solid #f59e0b;
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.active-order-warning .warning-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
  line-height: 1.4;
}

.active-order-warning .warning-content strong {
  display: block;
  color: #92400e;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.active-order-warning .warning-content p {
  margin: 0;
  color: #78350f;
  font-size: 0.85rem;
  line-height: 1.5;
}

.warning-order-id {
  font-weight: 700;
  color: #b45309;
}

.warning-status {
  font-weight: 600;
  text-transform: capitalize;
}

.checkout-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.checkout-btn {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  border: none;
  border-radius: 15px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.checkout-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(255, 71, 87, 0.4);
}

.checkout-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-cart {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.empty-cart-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-cart h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.empty-cart p {
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 30px;
}

.browse-menu-btn {
  padding: 15px 30px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  border: none;
  border-radius: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.browse-menu-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(255, 71, 87, 0.3);
}

/* Responsive Design */
@media (max-width: 992px) {
  .cart-content {
    grid-template-columns: 1fr;
  }
  
  .order-summary {
    position: static;
  }
}

@media (max-width: 768px) {
  .cart-item {
    grid-template-columns: 60px 1fr;
    gap: 10px;
  }
  
  .item-image {
    width: 60px;
    height: 60px;
  }
  
  .item-controls,
  .item-total,
  .remove-btn {
    grid-column: 2;
    justify-self: end;
    margin-top: 10px;
  }
  
  .payment-options {
    grid-template-columns: 1fr;
  }
  
  .address-input {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .cart-page {
    padding: 10px;
  }
  
  .cart-header h1 {
    font-size: 2rem;
  }
  
  .cart-items,
  .order-summary,
  .delivery-options,
  .payment-methods,
  .special-instructions,
  .checkout-section {
    padding: 20px;
  }
}

/* Modal Fade Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Order Confirmation Modal - GLOBAL STYLES (via teleport) */
/* Remove scoped to make this work at body level */


@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.confirmation-modal {
  background: white !important;
  border-radius: 25px;
  padding: 50px 40px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1000000 !important;
  pointer-events: auto !important;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.confirmation-success-icon {
  margin-bottom: 30px;
}

.checkmark-circle {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #4caf50, #66bb6a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.4);
}

@keyframes scaleIn {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

.checkmark {
  width: 40px;
  height: 70px;
  border: solid white;
  border-width: 0 6px 6px 0;
  transform: rotate(45deg);
  animation: drawCheck 0.5s ease-out 0.5s both;
}

@keyframes drawCheck {
  from {
    height: 0;
    width: 0;
  }
  to {
    height: 70px;
    width: 40px;
  }
}

.confirmation-title {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 30px;
  animation: fadeInUp 0.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.confirmation-details {
  background: #f8f9fa;
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 25px;
  animation: fadeInUp 0.5s ease-out 0.4s both;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #dee2e6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row.estimated-time {
  margin-top: 10px;
  padding-top: 20px;
  border-top: 2px solid #ff4757;
}

.detail-label {
  font-weight: 600;
  color: #666;
  font-size: 0.95rem;
}

.detail-value {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.detail-value.order-id {
  color: #ff4757;
  font-family: monospace;
  font-size: 0.9rem;
  background: #fff;
  padding: 5px 10px;
  border-radius: 8px;
}

.detail-value.total-amount {
  color: #4caf50;
  font-size: 1.3rem;
  font-weight: 700;
}

.confirmation-message {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  animation: fadeInUp 0.5s ease-out 0.5s both;
}

.confirmation-message p {
  margin: 8px 0;
  color: #2e7d32;
  font-weight: 600;
  font-size: 1rem;
}

.confirmation-btn {
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  border: none;
  border-radius: 15px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  animation: fadeInUp 0.5s ease-out 0.6s both;
}

.confirmation-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(255, 71, 87, 0.4);
}

/* Responsive for modal */
@media (max-width: 480px) {
  .confirmation-modal {
    padding: 40px 25px;
  }
  
  .confirmation-title {
    font-size: 1.6rem;
  }
  
  .checkmark-circle {
    width: 80px;
    height: 80px;
  }
  
  .checkmark {
    width: 30px;
    height: 50px;
  }
}
</style>

<style>
/* GLOBAL Modal Styles - Not Scoped (for teleport to body) */
.confirmation-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0, 0, 0, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 2147483647 !important; /* Maximum z-index */
  animation: fadeIn 0.3s ease-out !important;
  pointer-events: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
  margin: 0 !important;
  padding: 20px !important;
}

.confirmation-modal {
  background: white !important;
  border-radius: 25px !important;
  padding: 50px 40px !important;
  max-width: 500px !important;
  width: 90% !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;
  text-align: center !important;
  position: relative !important;
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
  z-index: 2147483647 !important;
  pointer-events: auto !important;
  margin: auto !important;
}

/* Voucher Summary Display */
.applied-voucher-summary {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  border: 2px solid #28a745;
  border-radius: 12px;
  padding: 15px;
  margin: 15px 0;
  animation: fadeIn 0.4s ease-out;
}

.voucher-info-row {
  margin-bottom: 12px;
}

.voucher-details {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.voucher-icon {
  font-size: 1.5rem;
  animation: pulse 2s infinite;
}

.voucher-name {
  font-weight: 700;
  color: #2e7d32;
  font-size: 1rem;
  margin-bottom: 4px;
}

.voucher-description {
  font-size: 0.85rem;
  color: #1b5e20;
  font-weight: 500;
}

.applied-voucher-summary .summary-row {
  margin-bottom: 0;
  padding-top: 10px;
  border-top: 1px solid rgba(46, 125, 50, 0.2);
}

.applied-voucher-summary .discount-amount {
  font-size: 1.1rem;
}

/* Animation for voucher appearance (if not already in your CSS) */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Pulse animation for voucher icon (if not already in your CSS) */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

</style>
